import React, { useState, useEffect, useMemo, Dispatch } from "react";
import { Card, Accordion, Row, Col } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useFormContext, get, useWatch, useFieldArray } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { RequestFormAction, RequestFormActionType, RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import { FormStepTypes, FormStepValidationTypes } from "constants/";
import testIds from "constants/testIds";
import { getCurrencies, getPackingListConfig } from "api";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { appendDefaultOption } from "helpers/commonHelpers";
import Alert from "components/molecules/Alert/Alert";
import PackingListGlobalConfiguration from "./components/PackingListGlobalConfiguration";
import FormHr from "../Common/FormHr";
import PackingListTable from "./components/PackingListTable";
import PackingListAddItem from "./components/PackingListAddItem";
import { dangerousGoodsOptions } from "./PackingListDefaultOptions";
import SpreadsheetImport from "./components/importPackingList/SpreadsheetImport";
import SpreadSheetPopover from "./components/importPackingList/SpreadSheetPopover";
import "./Packing.scss";
import { IDefaultCurrencyOption } from "./Types";
import { getPackingListMissingPickupOrDeliveryWarning } from "./packingListHelpers";

/**
 * A warning alert component that is displayed in the packing list accordion header.
 * It shows up in the following cases:
 *    1. If the packing list section has unsaved changes
 *    2. If the packing list section has no items
 */
function WarningAlert({ messageId, defaultMessage }: { messageId: string; defaultMessage: string }) {
  return (
    <div style={{ textTransform: "none" }} className="ml-3">
      <Alert variant="warning" iconClass="mr-2">
        <div>
          <FormattedMessage id={messageId} defaultMessage={defaultMessage} />
        </div>
      </Alert>
    </div>
  );
}

interface IPackingListProps {
  state: RequestFormState;
  dispatch: Dispatch<RequestFormAction>;
  validateSection?: Function;
  shouldValidateSection?: React.MutableRefObject<boolean>;
  isQuoteRequest?: boolean;
}

function PackingListForm({
  state,
  dispatch,
  validateSection,
  shouldValidateSection,
  isQuoteRequest = false,
}: IPackingListProps) {
  const [searchParams] = useSearchParams();
  const { getPaymentLocationLabel } = usePermissions();
  const currentActiveStep = searchParams.get("active_step");
  const intl = useIntl();
  const {
    getValues,
    setValue,
    formState: { errors },
    control,
    watch,
  } = useFormContext();
  const packingListFields = FormStepValidationTypes[FormStepTypes.PACKING_LIST] as string[];
  const hasError = packingListFields.some((item) => get(errors, item));

  const { hasWarnings: hasMissingStopsWarning } = getPackingListMissingPickupOrDeliveryWarning({ watch });

  const location = getValues("organizational.location");
  const { append, remove } = useFieldArray({
    control,
    name: "packing_list",
  });
  const packingListWatch = useWatch({
    name: "packing_list",
    control,
  });
  const [fetching, setFetching] = useState(false);
  const [packingListConfig, setPackingListConfig] = useState<any>({});

  const [currencyOptions, setCurrencyOptions] = useState<IDefaultCurrencyOption[]>([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);
  const [showSpreadsheetImport, setShowSpreadsheetImport] = useState(false);
  const [expandedRow, setExpandedRow] = useState<Row | null>(null);

  useEffect(() => retrievePackingList(), [location]);
  useEffect(() => retrieveCurrencies(), []);

  const groupedPackingListData = useMemo(() => {
    const packingListData = getValues("packing_list");
    if (!packingListData || !Object.keys(packingListConfig).length) return {};
    const grouped: Record<string, any[]> = {};
    packingListData.forEach((item: any, index: number) => {
      if (!item.handling_unit) return;
      if (!grouped[item.handling_unit]) grouped[item.handling_unit] = [];
      item.index = index;
      grouped[item.handling_unit].push(item);
    });
    return grouped;
  }, [packingListWatch, packingListConfig]);
  /**
   * Get the packing list configuration for the selected location
   */
  const retrievePackingList = () => {
    if (!location) return;
    setFetching(true);
    getPackingListConfig(location)
      .then((res) => {
        setPackingListConfig(res.data);

        // Format and set the handling unit options
        if (res.data?.handling_unit_options?.length) {
          const data: any = {};
          res.data.handling_unit_options.forEach((item: any) => {
            if (Object.keys(item)?.[0]) data[Object.keys(item)?.[0]] = item[Object.keys(item)?.[0]];
          });

          // Dispatch the handling unit options to the form state
          dispatch({
            type: RequestFormActionType.PACKING_DETAIL_SUCCESS,
            payload: { key: "handlingUnitOptions", value: data },
          });

          // Dispatch the NMFC options to the form state
          dispatch({
            type: RequestFormActionType.PACKING_DETAIL_SUCCESS,
            payload: { key: "nmfcOptions", value: res.data.nmfc_values },
          });
        }

        // If we should validate the section, validate it
        if (shouldValidateSection && validateSection && shouldValidateSection.current) {
          setTimeout(() => validateSection(FormStepTypes.PACKING_LIST), 400);
        }
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrievePackingList",
            defaultMessage: "Could not retrieve packing list settings. Please try again later.",
          }),
        ),
      )
      .finally(() => setFetching(false));
  };

  /**
   * Retrieve the currencies from the API
   */
  const retrieveCurrencies = () => {
    getCurrencies()
      .then((res) => {
        if (res.data) setCurrencyOptions(res.data);
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "new-request.packing-list.couldNotRetrieveCurrencies",
            defaultMessage: "Could not retrieve currencies. Please try again later.",
          }),
        ),
      );
    // Dispatch the dangerous good options to the form state
    dispatch({
      type: RequestFormActionType.PACKING_DETAIL_SUCCESS,
      payload: { key: "dangerousGoods", value: appendDefaultOption(dangerousGoodsOptions(intl), intl) },
    });
  };

  return (
    <>
      <SpreadsheetImport
        packingListConfig={packingListConfig}
        currencyOptions={currencyOptions}
        isOpen={showSpreadsheetImport}
        onClose={() => setShowSpreadsheetImport(false)}
        setValue={setValue}
      />
      <Card className="mb-3 form-container" data-testid={testIds.REQUEST_FORM_PACKING_LIST_SECTION}>
        {/* Packing list accordion header */}
        <Accordion.Toggle as={Card.Header} className="accordion-header d-block" eventKey={FormStepTypes.PACKING_LIST}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              {(hasError || state.draftSaved[FormStepTypes.PACKING_LIST]) && (
                <div className={`valid-status ${hasError ? "error" : ""}`}>
                  {hasError ? <ExclamationIcon /> : <CheckIcon />}
                </div>
              )}
              <h5>
                <FormattedMessage id="general.packingList" defaultMessage="Packing list" />
              </h5>
              {location && packingListConfig?.bulk_upload_enabled && (
                <div className="position-relative ml-2">
                  <SpreadSheetPopover
                    packingListConfig={packingListConfig}
                    setShowSpreadsheetImport={setShowSpreadsheetImport}
                  />
                </div>
              )}

              {!!packingListWatch?.length && state.unsavedChanges[FormStepTypes.PACKING_LIST] && (
                <WarningAlert
                  messageId="new-request.packing-list.unsavedWarning"
                  defaultMessage="You have unsaved changes"
                />
              )}
              {!packingListWatch?.length && state.unsavedChanges[FormStepTypes.PACKING_LIST] && (
                <WarningAlert messageId="new-request.packing-list.noEntryAdded" defaultMessage="No entry added" />
              )}
            </div>

            <SortIcon
              fill="#000"
              width={8}
              height={7}
              transform={`rotate(${currentActiveStep === FormStepTypes.PACKING_LIST ? 180 : 0})`}
            />
          </div>

          {hasMissingStopsWarning && (
            <Row>
              <Col>
                <Alert variant="warning" className="mt-lg">
                  <FormattedMessage
                    id="new-request.schedule.packingList.error.missingPickupOrDelivery"
                    defaultMessage="Some stops were removed. Please update the commodities with a new stop"
                  />
                </Alert>
              </Col>
            </Row>
          )}
        </Accordion.Toggle>
        {/* Packing list accordion body */}
        <Accordion.Collapse eventKey={FormStepTypes.PACKING_LIST}>
          <>
            {/* No payment location selected warning */}
            {!fetching && !location && (
              <Row>
                <Col>
                  <Alert variant="warning" className="mb-3 ml-3">
                    <FormattedMessage
                      id="new-request.organisational-details.selectPaymentLocationPrompt"
                      defaultMessage="Please select a {paymentLocation} before continuing"
                      values={{ paymentLocation: getPaymentLocationLabel() }}
                    />
                  </Alert>
                </Col>
              </Row>
            )}

            {/* Loading skeleton */}
            {fetching && (
              <Card.Body>
                <Row>
                  <Col>
                    <Alert variant="warning" className="mb-3 ml-3">
                      <FormattedMessage
                        id="new-request.organisational-details.selectPaymentLocationPrompt"
                        defaultMessage="Please select a {paymentLocation} before continuing"
                        values={{ paymentLocation: getPaymentLocationLabel() }}
                      />
                    </Alert>
                  </Col>
                </Row>
              </Card.Body>
            )}
            {/* Packing list form */}
            {!fetching && location && !!Object.keys(packingListConfig).length && (
              <Card.Body>
                <PackingListGlobalConfiguration packingListSettings={packingListConfig} state={state} />
                <FormHr className="mt-4" />
                <div className="packing-list-form-container">
                  {Object.keys(groupedPackingListData).map((unit) => (
                    <PackingListTable
                      key={unit}
                      items={groupedPackingListData[unit]}
                      append={append}
                      remove={remove}
                      isNMFCEnabled={packingListConfig.nmfc_number_enabled}
                      NMFCValues={packingListConfig.nmfc_values}
                      handlingUnits={packingListConfig.handling_unit_options}
                      handlingRequiredSetting={packingListConfig.handling_required_enabled}
                      selectedItemIndex={selectedItemIndex}
                      setSelectedItemIndex={setSelectedItemIndex}
                      currencyOptions={currencyOptions}
                      packingListConfig={packingListConfig}
                      state={state}
                      dispatch={dispatch}
                      expandedRow={expandedRow}
                      setExpandedRow={setExpandedRow}
                      isQuoteRequest={isQuoteRequest}
                    />
                  ))}
                  {selectedItemIndex == null && (
                    <PackingListAddItem
                      handlingUnits={packingListConfig.handling_unit_options}
                      isNMFCEnabled={packingListConfig.nmfc_number_enabled}
                      NMFCValues={packingListConfig.nmfc_values}
                      additionalInfoPlaceholder={packingListConfig.additional_info_description}
                      append={append}
                      selectedItemIndex={selectedItemIndex}
                      setSelectedItemIndex={setSelectedItemIndex}
                      getRequestFormValues={getValues}
                      watchRequestFormValues={watch}
                      setRequestFormValue={setValue}
                      currencyOptions={currencyOptions}
                      state={state}
                      dispatch={dispatch}
                      isQuoteRequest={isQuoteRequest}
                    />
                  )}
                </div>
              </Card.Body>
            )}
          </>
        </Accordion.Collapse>
      </Card>
    </>
  );
}

PackingListForm.defaultProps = {
  validateSection: undefined,
  shouldValidateSection: undefined,
  isQuoteRequest: false,
};

export default PackingListForm;
